import type { AmrapPhase, Challenge, ChallengeWeek } from './workout'

/** Una sesión terminada y guardada. Es la unidad que cuenta para el premio. */
export interface SessionRecord {
  readonly id: string
  readonly challengeId: string
  readonly weekNumber: number
  /** Fecha de finalización en ISO 8601. */
  readonly completedAt: string
  /** Rondas completas del AMRAP puntuado. */
  readonly rounds: number
  /** Repeticiones sueltas de la ronda que quedó a medias. */
  readonly reps: number
  /** Tiempo del finisher AFAP en segundos, si lo hubo. */
  readonly afapSeconds: number | null
  readonly crewBonus: boolean
  /** Lo que suma al contador: 1, o 2 con Crew Bonus. */
  readonly credits: number
}

export interface Progress {
  readonly challengeId: string
  readonly sessions: readonly SessionRecord[]
}

export const emptyProgress = (challengeId: string): Progress => ({ challengeId, sessions: [] })

export function totalCredits(progress: Progress): number {
  return progress.sessions.reduce((total, session) => total + session.credits, 0)
}

export function sessionsOfWeek(progress: Progress, weekNumber: number): SessionRecord[] {
  return progress.sessions.filter((session) => session.weekNumber === weekNumber)
}

export function crewBonusUsed(progress: Progress): number {
  return progress.sessions.filter((session) => session.crewBonus).length
}

/** Sesiones que faltan para el premio grande. */
export function creditsRemaining(challenge: Challenge, progress: Progress): number {
  return Math.max(0, challenge.targetSessions - totalCredits(progress))
}

/** El premio de mayor exigencia que ya está conseguido, si hay alguno. */
export function prizeReached(challenge: Challenge, progress: Progress) {
  const credits = totalCredits(progress)
  return (
    [...challenge.prizes].sort((a, b) => b.sessions - a.sessions).find((prize) => credits >= prize.sessions) ?? null
  )
}

/** El bloque puntuado de una semana: el AMRAP cuya marca se guarda. */
export function scoredPhase(week: ChallengeWeek): AmrapPhase | null {
  const amraps = week.workout.phases.filter((phase): phase is AmrapPhase => phase.kind === 'amrap')
  return amraps.find((phase) => phase.scored) ?? amraps[0] ?? null
}

/**
 * Convierte una marca en un número comparable: repeticiones totales.
 * Necesita saber cuántas reps tiene una ronda del AMRAP puntuado.
 */
export function amrapScore(record: Pick<SessionRecord, 'rounds' | 'reps'>, repsPerRound: number): number {
  return record.rounds * repsPerRound + record.reps
}

export function repsPerRound(phase: AmrapPhase): number {
  return phase.movements.reduce((total, movement) => total + movement.reps, 0)
}

/** La mejor marca registrada en una semana concreta, para comparar en el test. */
export function bestRecordOfWeek(
  progress: Progress,
  weekNumber: number,
  reps: number,
): SessionRecord | null {
  const candidates = sessionsOfWeek(progress, weekNumber)
  if (candidates.length === 0) return null
  return candidates.reduce((best, session) =>
    amrapScore(session, reps) > amrapScore(best, reps) ? session : best,
  )
}
